/**
 * useManualEntryHandlers Hook
 * Shared hook for manual CPT and delivery charge entry handling
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { CPTSlabForManualOption } from "../../../views/Orders/enums";
import { DeliverySupplierOption, ManualDeliveryOptionEntry } from "../types";
import {
  minutesToHours,
  calculateFutureDate,
  parseQuickEtaMins,
} from "../utils/deliveryTimeUtils";
import {
  dateSecondisBeforeDateFirst,
  minusFiveMinutesFromCurrentTime,
} from "../../../utils/dateResolvers";
import {
  filterSupplierOptionsForManualCptSlab,
  isManualFulfillmentSupplierValidForCptSlab,
  resolveManualFulfillmentSupplierForCptSlab,
} from "../utils/resolveManualFulfillmentSupplierForCptSlab";
import { TNG_SUPPLIER_ID } from "../utils/isVbzCustomerTngSplitCase";

export interface QuickEtaOption {
  label: string;
  minutes: number;
}

interface UseManualEntryHandlersProps {
  isExpired: boolean;
  quickEtasMins?: string;
  currentManualEntry?: ManualDeliveryOptionEntry;
  onManualEntryChange: (entry: ManualDeliveryOptionEntry) => void;
  supplierOptions?: DeliverySupplierOption[];
  isManualFulfillmentRoutingRestricted?: boolean;
  isVbzCustomerTngSplitCase?: boolean;
}

export function useManualOptionHandlers({
  isExpired,
  quickEtasMins,
  currentManualEntry,
  onManualEntryChange,
  supplierOptions,
  isManualFulfillmentRoutingRestricted = false,
  isVbzCustomerTngSplitCase = false,
}: UseManualEntryHandlersProps) {
  // Reference time for validation (set once on mount)
  const [cptGivenTime] = useState(() => minusFiveMinutesFromCurrentTime());

  // Parse quick ETA options from config
  const quickEtaOptions: QuickEtaOption[] = useMemo(() => {
    const minutesList = parseQuickEtaMins(quickEtasMins);
    return minutesList.map((minutes) => ({
      label: minutesToHours(minutes),
      minutes: minutes,
    }));
  }, [quickEtasMins]);

  // Keep fulfillment supplier aligned with CPT when SIC is restricted.
  useEffect(() => {
    const cptSlab = currentManualEntry?.cptSlab;
    if (cptSlab == null) {
      return;
    }

    if (isVbzCustomerTngSplitCase) {
      if (currentManualEntry?.supplierId === TNG_SUPPLIER_ID) {
        return;
      }
      onManualEntryChange({
        cpt: currentManualEntry?.cpt || "",
        deliveryCharge: currentManualEntry?.deliveryCharge ?? 0,
        cptSlab,
        supplierId: TNG_SUPPLIER_ID,
      });
      return;
    }

    if (!isManualFulfillmentRoutingRestricted) {
      return;
    }

    const lockedSupplierId = resolveManualFulfillmentSupplierForCptSlab(
      cptSlab,
      supplierOptions,
    );
    const currentSupplierId = currentManualEntry?.supplierId;
    const isValid = isManualFulfillmentSupplierValidForCptSlab({
      cptSlab,
      supplierId: currentSupplierId,
      supplierOptions,
      isRestricted: true,
    });

    if (lockedSupplierId) {
      if (isValid && currentSupplierId === lockedSupplierId) {
        return;
      }
      onManualEntryChange({
        cpt: currentManualEntry?.cpt || "",
        deliveryCharge: currentManualEntry?.deliveryCharge ?? 0,
        cptSlab,
        supplierId: lockedSupplierId,
      });
      return;
    }

    // SP/SD with no AB available — clear invalid SIC selection.
    if (currentSupplierId && !isValid) {
      onManualEntryChange({
        cpt: currentManualEntry?.cpt || "",
        deliveryCharge: currentManualEntry?.deliveryCharge ?? 0,
        cptSlab,
        supplierId: undefined,
      });
    }
  }, [
    isVbzCustomerTngSplitCase,
    isManualFulfillmentRoutingRestricted,
    currentManualEntry?.cpt,
    currentManualEntry?.deliveryCharge,
    currentManualEntry?.cptSlab,
    currentManualEntry?.supplierId,
    supplierOptions,
    onManualEntryChange,
  ]);

  // Handle CPT change with validation
  const handleManualCPTChange = useCallback(
    (value: string) => {
      if (value && dateSecondisBeforeDateFirst(cptGivenTime, value)) {
        alert("ETA time can not be less than ETA given time");
        return;
      }
      const current = currentManualEntry || {
        cpt: "",
        deliveryCharge: 0,
      };
      onManualEntryChange({ ...current, cpt: value });
    },
    [cptGivenTime, currentManualEntry, onManualEntryChange]
  );

  // Handle delivery charge change
  const handleManualChargeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(event.target.value, 10) || 0;
      const current = currentManualEntry || {
        cpt: "",
        deliveryCharge: 0,
      };
      onManualEntryChange({ ...current, deliveryCharge: value });
    },
    [currentManualEntry, onManualEntryChange],
  );

  const handleManualCPTSlabChange = useCallback(
    (value: CPTSlabForManualOption) => {
      const current = currentManualEntry || {
        cpt: "",
        deliveryCharge: 0,
      };
      const lockedSupplierId = isVbzCustomerTngSplitCase
        ? TNG_SUPPLIER_ID
        : isManualFulfillmentRoutingRestricted
          ? resolveManualFulfillmentSupplierForCptSlab(value, supplierOptions)
          : undefined;
      onManualEntryChange({
        ...current,
        cptSlab: value,
        ...(lockedSupplierId
          ? { supplierId: lockedSupplierId }
          : isManualFulfillmentRoutingRestricted
            ? { supplierId: undefined }
            : {}),
      });
    },
    [
      currentManualEntry,
      onManualEntryChange,
      isVbzCustomerTngSplitCase,
      isManualFulfillmentRoutingRestricted,
      supplierOptions,
    ],
  );

  // Handle quick ETA tag selection
  const handleQuickEtaSelect = useCallback(
    (minutes: number) => {
      if (isExpired) return;
      const futureDateStr = calculateFutureDate(minutes);
      handleManualCPTChange(futureDateStr);
    },
    [isExpired, handleManualCPTChange]
  );

  const handleManualSupplierChange = useCallback(
    (value: string) => {
      if (isVbzCustomerTngSplitCase) {
        if (value !== TNG_SUPPLIER_ID) {
          return;
        }
      } else if (isManualFulfillmentRoutingRestricted) {
        const cptSlab = currentManualEntry?.cptSlab;
        if (cptSlab == null) {
          return;
        }
        const allowed = filterSupplierOptionsForManualCptSlab(
          cptSlab,
          supplierOptions,
          true,
        );
        if (!allowed.some((opt) => opt.supplierId === value)) {
          return;
        }
      }
      const current = currentManualEntry || {
        cpt: "",
        deliveryCharge: 0,
      };
      onManualEntryChange({ ...current, supplierId: value });
    },
    [
      currentManualEntry,
      onManualEntryChange,
      isVbzCustomerTngSplitCase,
      isManualFulfillmentRoutingRestricted,
      supplierOptions,
    ],
  );

  return {
    cptGivenTime,
    quickEtaOptions,
    handleManualCPTChange,
    handleManualChargeChange,
    handleQuickEtaSelect,
    handleManualCPTSlabChange,
    handleManualSupplierChange,
  };
}
